"use client";

import * as React from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";
import { cn, formatNumber } from "@gerama/utils";
import { Button } from "./button";
import { Input } from "./input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";

const DEFAULT_LIMIT_OPTIONS = [10, 20, 50, 100];

interface ListPaginationProps {
  page: number;
  limit: number;
  count: number;
  onPageChange: (page: number) => void;
  /** Formats the "N item" label — kept caller-side so each resource can localize its own noun. */
  countLabel?: (count: number) => string;
  /** Shows the page-size select when given. */
  onLimitChange?: (limit: number) => void;
  limitOptions?: number[];
  className?: string;
}

/**
 * Page numbers to render: always the first and last page, a window of two
 * around the current one, and `"…"` for the gaps — `1 … 4 5 [6] 7 8 … 42`.
 */
export function paginationRange(page: number, lastPage: number, siblings = 2): (number | "…")[] {
  const pages = new Set<number>([1, lastPage]);
  for (let p = page - siblings; p <= page + siblings; p++) {
    if (p >= 1 && p <= lastPage) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const out: (number | "…")[] = [];
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1];
    if (prev !== undefined && p - prev > 1) {
      // A gap of exactly one page is shown as that page, not as "…".
      if (p - prev === 2) out.push(prev + 1);
      else out.push("…");
    }
    out.push(p);
  });
  return out;
}

/**
 * Shared pager: first/prev/numbered pages/next/last, a "showing X–Y of N"
 * label, optional page-size select and a jump-to-page box. RTL: "previous"
 * points right, like the rest of the admin's arrows.
 */
export function ListPagination({
  page,
  limit,
  count,
  onPageChange,
  countLabel,
  onLimitChange,
  limitOptions = DEFAULT_LIMIT_OPTIONS,
  className,
}: ListPaginationProps) {
  const lastPage = Math.max(1, Math.ceil(count / limit));
  const current = Math.min(Math.max(1, page), lastPage);
  const from = count === 0 ? 0 : (current - 1) * limit + 1;
  const to = Math.min(count, current * limit);
  const [jump, setJump] = React.useState("");

  const go = (target: number) => {
    const next = Math.min(Math.max(1, target), lastPage);
    if (next !== current) onPageChange(next);
  };

  return (
    <div
      className={cn(
        "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
        className,
      )}
    >
      <div className="text-muted-foreground flex flex-wrap items-center gap-3 text-sm">
        <span>
          {/* The label already carries the count ("۲ ادمین"); a "·" separator read as a Persian zero. */}
          نمایش {formatNumber(from, "fa")}–{formatNumber(to, "fa")} از{" "}
          {countLabel ? countLabel(count) : formatNumber(count, "fa")}
        </span>
        {onLimitChange && (
          <span className="flex items-center gap-2">
            <span>در هر صفحه</span>
            <Select value={String(limit)} onValueChange={(v) => onLimitChange(Number(v))}>
              <SelectTrigger size="sm" className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {limitOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {formatNumber(option, "fa")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </span>
        )}
      </div>

      {lastPage > 1 && (
        <nav aria-label="صفحه‌بندی" className="flex flex-wrap items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label="صفحهٔ اول"
            disabled={current <= 1}
            onClick={() => go(1)}
          >
            <ChevronsRightIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="صفحهٔ قبل"
            disabled={current <= 1}
            onClick={() => go(current - 1)}
          >
            <ChevronRightIcon />
          </Button>
          {paginationRange(current, lastPage).map((item, i) =>
            item === "…" ? (
              <span key={`gap-${i}`} className="text-muted-foreground px-1">
                …
              </span>
            ) : (
              <Button
                key={item}
                variant={item === current ? "default" : "outline"}
                size="icon"
                aria-label={`صفحهٔ ${item}`}
                aria-current={item === current ? "page" : undefined}
                onClick={() => go(item)}
              >
                {formatNumber(item, "fa")}
              </Button>
            ),
          )}
          <Button
            variant="outline"
            size="icon"
            aria-label="صفحهٔ بعد"
            disabled={current >= lastPage}
            onClick={() => go(current + 1)}
          >
            <ChevronLeftIcon />
          </Button>
          <Button
            variant="outline"
            size="icon"
            aria-label="صفحهٔ آخر"
            disabled={current >= lastPage}
            onClick={() => go(lastPage)}
          >
            <ChevronsLeftIcon />
          </Button>
          {lastPage > 7 && (
            <form
              className="ms-2 flex items-center gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                const target = Number(
                  jump.replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d))),
                );
                if (Number.isFinite(target) && target > 0) go(target);
                setJump("");
              }}
            >
              <Input
                inputMode="numeric"
                placeholder="برو به"
                aria-label="رفتن به صفحه"
                className="h-9 w-20"
                value={jump}
                onChange={(e) => setJump(e.target.value.replace(/[^\d۰-۹]/g, ""))}
              />
            </form>
          )}
        </nav>
      )}
    </div>
  );
}
